import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUser, getMilestoneStats, getPremiumStatus, getPreferences } from '../lib/api';
import { LOGO_URL, SKETCH_PATTERNS } from '../lib/images';
import useCurrency, { SUPPORTED_CURRENCIES, getCurrencyOverride, setCurrencyOverride } from '../hooks/useCurrency';
import { Loader2, User, Mail, Trophy, Star, Settings, Crown, ChevronRight, Globe } from 'lucide-react';

export default function ProfilePage() {
  const { user, deviceId, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [premium, setPremium] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [userRes, mileRes, premRes, prefRes] = await Promise.allSettled([
          getUser(deviceId, user?.id || user?._id),
          getMilestoneStats(deviceId),
          getPremiumStatus(deviceId),
          getPreferences(deviceId),
        ]);
        if (userRes.status === 'fulfilled') setProfile(userRes.value.data);
        if (mileRes.status === 'fulfilled') setMilestones(mileRes.value.data);
        if (premRes.status === 'fulfilled') setPremium(premRes.value.data);
        if (prefRes.status === 'fulfilled') setPreferences(prefRes.value.data);
      } catch {}
      setLoading(false);
    }
    load();
  }, [deviceId, user]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  const userName = user?.name || profile?.name || 'User';
  const userEmail = user?.email || profile?.email || '';
  const points = profile?.points || milestones?.points || 0;
  const earnedMilestones = milestones?.earned_milestones || milestones?.milestones_earned || [];

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="profile-page">
      <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Profile</h1>

      {/* User Card */}
      <div className="rounded-lg p-6 border mb-6 animate-fade-in"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="profile-card">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold"
            style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
            {userName[0]?.toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{userName}</h2>
            <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--muted-fg)' }}>
              <Mail className="w-4 h-4" /> {userEmail}
            </div>
          </div>
          {premium?.is_premium && (
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg"
              style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}>
              <Crown className="w-4 h-4" />
              <span className="text-sm font-semibold">Premium</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="rounded-lg p-5 border animate-fade-in stagger-1" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <Star className="w-5 h-5 mb-2" style={{ color: 'var(--accent)' }} />
          <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{points}</p>
          <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Points Earned</p>
        </div>
        <div className="rounded-lg p-5 border animate-fade-in stagger-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <Trophy className="w-5 h-5 mb-2" style={{ color: 'var(--primary)' }} />
          <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{earnedMilestones.length}</p>
          <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Milestones</p>
        </div>
        <div className="rounded-lg p-5 border animate-fade-in stagger-3" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <Settings className="w-5 h-5 mb-2" style={{ color: 'var(--secondary-fg)' }} />
          <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            {profile?.active_programs?.length || 0}
          </p>
          <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Active Programs</p>
        </div>
      </div>

      {/* Milestones */}
      {earnedMilestones.length > 0 && (
        <div className="rounded-lg p-6 border mb-6 animate-fade-in stagger-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <h3 className="font-bold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Earned Milestones</h3>
          <div className="flex flex-wrap gap-3">
            {earnedMilestones.map((m, i) => (
              <div key={i} className="px-3 py-2 rounded-lg text-xs font-medium"
                style={{ background: 'var(--secondary)', color: 'var(--secondary-fg)' }}>
                {typeof m === 'string' ? m : m.name || m.milestone_id || `Milestone ${i + 1}`}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Meal Preferences */}
      {preferences && (
        <div className="rounded-lg p-6 border mb-6 animate-fade-in stagger-5" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="preferences-section">
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Meal Preferences</h3>
            <Link to="/onboarding?edit=true" className="text-xs font-semibold hover:underline" style={{ color: 'var(--primary)' }} data-testid="edit-meal-prefs">
              Edit
            </Link>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>Dietary</p>
              <p className="text-sm font-medium capitalize" style={{ color: 'var(--text)' }}>
                {(preferences.meal_preferences?.dietary || preferences.dietary)?.replace(/_/g, ' ') || 'Not set'}
              </p>
            </div>
            {(preferences.meal_preferences?.cuisines || preferences.cuisines)?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>Cuisines</p>
                <div className="flex flex-wrap gap-2">
                  {(preferences.meal_preferences?.cuisines || preferences.cuisines || []).map((c, i) => (
                    <span key={i} className="px-2 py-1 rounded text-xs capitalize"
                      style={{ background: 'var(--muted)', color: 'var(--muted-fg)' }}>{c.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
            {(preferences.meal_preferences?.allergens || preferences.allergens)?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>Avoiding</p>
                <div className="flex flex-wrap gap-2">
                  {(preferences.meal_preferences?.allergens || preferences.allergens || []).map((a, i) => (
                    <span key={i} className="px-2 py-1 rounded text-xs capitalize"
                      style={{ background: '#FEE2E2', color: '#B91C1C' }}>{a.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Currency Preference */}
      <CurrencySection />

      {/* Premium Status */}
      {premium && !premium.is_premium && (
        <Link to="/premium" className="block rounded-lg p-6 border animate-fade-in stagger-6 transition-all hover:-translate-y-0.5 hover:shadow-md"
          style={{ background: 'linear-gradient(135deg, #879B7D15 0%, #E2725B15 100%)', borderColor: 'var(--accent)' + '40' }} data-testid="profile-premium-cta">
          <div className="flex items-center gap-3 mb-3">
            <Crown className="w-6 h-6" style={{ color: 'var(--accent)' }} />
            <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Upgrade to Premium</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--muted-fg)' }}>
            Unlock mood insights, unlimited gratitude entries, personality test, and all wellness programs.
          </p>
          <div className="flex items-center gap-1 text-sm font-medium" style={{ color: 'var(--accent)' }}>
            Get Premium <ChevronRight className="w-4 h-4" />
          </div>
        </Link>
      )}
    </div>
  );
}

function CurrencySection() {
  const currency = useCurrency();
  const [choice, setChoice] = useState(getCurrencyOverride());

  const handleChange = (code) => {
    setChoice(code);
    setCurrencyOverride(code);
  };

  const autoDetectedLabel =
    currency.isManualOverride ? 'Auto (location-based)' : `Auto · ${currency.currency}`;

  return (
    <div className="rounded-lg p-6 border mb-6 animate-fade-in stagger-5"
      style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
      data-testid="currency-section">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-4 h-4" style={{ color: 'var(--primary)' }} />
        <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Billing Currency</h3>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--muted-fg)' }}>
        Prices and Razorpay charges are shown in this currency. Defaults to USD; auto-detects your region if possible.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <select value={choice} onChange={(e) => handleChange(e.target.value)}
          data-testid="currency-select"
          className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border text-sm cursor-pointer focus:outline-none focus:ring-2"
          style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--text)' }}>
          <option value="AUTO">{autoDetectedLabel}</option>
          {SUPPORTED_CURRENCIES.map(c => (
            <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</option>
          ))}
        </select>
        <div className="text-sm px-3 py-2 rounded-lg"
          style={{ background: 'var(--primary)' + '10', color: 'var(--primary)', minWidth: 110, textAlign: 'center' }}
          data-testid="currency-example">
          Sample: <span className="font-bold">{currency.format(2999)}</span>
        </div>
      </div>
    </div>
  );
}

